import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Form, 
  Input, 
  Button, 
  Switch, 
  Select, 
  InputNumber,
  message, 
  Space, 
  Typography, 
  Row, 
  Col, 
  Divider,
  Tabs,
  Upload,
  ColorPicker,
  Slider,
  Alert,
  Modal,
  Tag
} from 'antd';
import { 
  SettingOutlined, 
  SaveOutlined, 
  ReloadOutlined,
  UploadOutlined,
  BgColorsOutlined,
  BellOutlined,
  SecurityScanOutlined,
  GlobalOutlined,
  MailOutlined,
  DollarOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import { 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc,
  collection,
  query,
  getDocs
} from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { theme } from '../../../styles/theme';
import { trackAdminEvent, ANALYTICS_EVENTS } from '../../../utils/analytics';

const { Title, Text } = Typography;
const { Option } = Select;
const { TabPane } = Tabs;
const { TextArea } = Input;

const AdminSettings = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState({});
  const [hasChanges, setHasChanges] = useState(false);

  // Default settings structure
  const defaultSettings = {
    // General Settings
    siteName: 'HSP - Home Services',
    siteDescription: 'Affordable, unique home services packages. Professional home maintenance and repair services.',
    siteUrl: 'https://hammersawpaint.com',
    contactEmail: 'info@hammersawpaint.com',
    contactPhone: '(555) 123-4567',
    
    // Business Settings
    businessHours: 'Monday - Friday: 8:00 AM - 6:00 PM',
    serviceArea: 'Greater San Francisco Bay Area',
    defaultQuoteAmount: 400,
    currency: 'USD',
    
    // Email Settings
    emailNotifications: true,
    quoteNotificationEmail: 'quotes@hammersawpaint.com',
    adminNotificationEmail: 'admin@hammersawpaint.com',
    emailTemplate: 'default',
    
    // Theme Settings
    primaryColor: '#FD865C',
    secondaryColor: '#2C3E50',
    accentColor: '#E74C3C',
    fontFamily: 'Futura, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    
    // Feature Toggles
    enableReferralProgram: true,
    enableEmailMarketing: true,
    enableAnalytics: true,
    enableUserRegistration: true,
    enableQuoteSystem: true,
    
    // Security Settings
    requireEmailVerification: false,
    allowGuestQuotes: true,
    maxQuotesPerUser: 5,
    sessionTimeout: 30, // minutes
    
    // SEO Settings
    metaTitle: 'HSP - Professional Home Services',
    metaDescription: 'Professional home maintenance and repair services. Get your free quote today!',
    metaKeywords: 'home services, maintenance, repair, painting, cleaning',
    
    // Social Media
    facebookUrl: '',
    twitterUrl: '',
    instagramUrl: '',
    linkedinUrl: '',
    
    // Maintenance Mode
    maintenanceMode: false,
    maintenanceMessage: 'We are currently performing scheduled maintenance. Please check back soon.',
    
    // Backup Settings
    autoBackup: true,
    backupFrequency: 'daily',
    backupRetention: 30 // days
  };

  // Load settings from Firestore
  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const settingsDoc = await getDoc(doc(db, 'settings', 'general'));
      
      if (settingsDoc.exists()) {
        const settingsData = settingsDoc.data();
        setSettings({ ...defaultSettings, ...settingsData });
        form.setFieldsValue({ ...defaultSettings, ...settingsData });
      } else {
        // Create default settings if they don't exist
        await setDoc(doc(db, 'settings', 'general'), defaultSettings);
        setSettings(defaultSettings);
        form.setFieldsValue(defaultSettings);
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      message.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async (values) => {
    try {
      setLoading(true);
      
      // Update settings in Firestore
      await updateDoc(doc(db, 'settings', 'general'), {
        ...values,
        lastUpdated: new Date(),
        updatedBy: 'admin' // In a real app, you'd get this from auth context
      });
      
      setSettings(values);
      setHasChanges(false);
      
      trackAdminEvent(ANALYTICS_EVENTS.SETTINGS_UPDATED, {
        settingsUpdated: Object.keys(values),
        timestamp: new Date().toISOString()
      });
      
      message.success('Settings saved successfully!');
    } catch (error) {
      console.error('Error saving settings:', error);
      message.error('Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  const handleResetSettings = () => {
    Modal.confirm({
      title: 'Reset Settings',
      content: 'Are you sure you want to reset all settings to default values? This action cannot be undone.',
      okText: 'Reset',
      okType: 'danger',
      onOk: async () => {
        try {
          await setDoc(doc(db, 'settings', 'general'), defaultSettings);
          setSettings(defaultSettings);
          form.setFieldsValue(defaultSettings);
          setHasChanges(false);
          message.success('Settings reset to default values');
        } catch (error) {
          console.error('Error resetting settings:', error);
          message.error('Failed to reset settings');
        }
      }
    });
  };

  const handleFormChange = () => {
    setHasChanges(true);
  };

  const handleExportSettings = () => {
    const dataStr = JSON.stringify(settings, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'hsp-settings.json';
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleImportSettings = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const importedSettings = JSON.parse(e.target.result);
        setSettings(importedSettings);
        form.setFieldsValue(importedSettings);
        setHasChanges(true);
        message.success('Settings imported successfully');
      } catch (error) {
        message.error('Invalid settings file');
      }
    };
    reader.readAsText(file);
    return false; // Prevent upload
  };

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', width: '100%' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <Title level={2} style={{ 
          color: theme.colors.black, 
          fontFamily: theme.fonts.primary,
          marginBottom: '8px'
        }}>
          <SettingOutlined /> Admin Settings
        </Title>
        <Text style={{ color: theme.colors.gray, fontFamily: theme.fonts.primary }}>
          Configure application settings and preferences
        </Text>
      </div>

      {/* Settings Form */}
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSaveSettings}
        onValuesChange={handleFormChange}
        initialValues={defaultSettings}
      >
        <Tabs defaultActiveKey="general" type="card">
          {/* General Settings */}
          <TabPane tab={<span><GlobalOutlined />General</span>} key="general">
            <Card title="General Settings" style={{ marginBottom: '24px' }}>
              <Row gutter={[16, 16]}>
                <Col span={12}>
                  <Form.Item
                    name="siteName"
                    label="Site Name"
                    rules={[{ required: true, message: 'Please enter site name' }]}
                  >
                    <Input placeholder="Enter site name" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="siteUrl"
                    label="Site URL"
                    rules={[{ required: true, message: 'Please enter site URL' }]}
                  >
                    <Input placeholder="https://example.com" />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item
                    name="siteDescription"
                    label="Site Description"
                    rules={[{ required: true, message: 'Please enter site description' }]}
                  >
                    <TextArea rows={3} placeholder="Enter site description" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="contactEmail"
                    label="Contact Email"
                    rules={[{ required: true, type: 'email', message: 'Please enter valid email' }]}
                  >
                    <Input placeholder="contact@example.com" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="contactPhone"
                    label="Contact Phone"
                    rules={[{ required: true, message: 'Please enter contact phone' }]}
                  >
                    <Input placeholder="(555) 123-4567" />
                  </Form.Item>
                </Col>
              </Row>
            </Card>

            <Card title="Business Settings" style={{ marginBottom: '24px' }}>
              <Row gutter={[16, 16]}>
                <Col span={12}>
                  <Form.Item
                    name="businessHours"
                    label="Business Hours"
                  >
                    <Input placeholder="Monday - Friday: 8:00 AM - 6:00 PM" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="serviceArea"
                    label="Service Area"
                  >
                    <Input placeholder="Greater San Francisco Bay Area" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="defaultQuoteAmount"
                    label="Default Quote Amount"
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      placeholder="400"
                      min={0}
                      formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                      parser={value => value.replace(/\$\s?|(,*)/g, '')}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="currency"
                    label="Currency"
                  >
                    <Select>
                      <Option value="USD">USD ($)</Option>
                      <Option value="EUR">EUR (€)</Option>
                      <Option value="GBP">GBP (£)</Option>
                      <Option value="CAD">CAD (C$)</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          </TabPane>

          {/* Email Settings */}
          <TabPane tab={<span><MailOutlined />Email</span>} key="email">
            <Card title="Email Settings" style={{ marginBottom: '24px' }}>
              <Row gutter={[16, 16]}>
                <Col span={24}>
                  <Form.Item
                    name="emailNotifications"
                    label="Enable Email Notifications"
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="quoteNotificationEmail"
                    label="Quote Notification Email"
                    rules={[{ type: 'email', message: 'Please enter valid email' }]}
                  >
                    <Input placeholder="quotes@example.com" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="adminNotificationEmail"
                    label="Admin Notification Email"
                    rules={[{ type: 'email', message: 'Please enter valid email' }]}
                  >
                    <Input placeholder="admin@example.com" />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item
                    name="emailTemplate"
                    label="Email Template"
                  >
                    <Select>
                      <Option value="default">Default Template</Option>
                      <Option value="modern">Modern Template</Option>
                      <Option value="minimal">Minimal Template</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          </TabPane>

          {/* Theme Settings */}
          <TabPane tab={<span><BgColorsOutlined />Theme</span>} key="theme">
            <Card title="Theme Settings" style={{ marginBottom: '24px' }}>
              <Row gutter={[16, 16]}>
                <Col span={8}>
                  <Form.Item
                    name="primaryColor"
                    label="Primary Color"
                  >
                    <ColorPicker showText />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name="secondaryColor"
                    label="Secondary Color"
                  >
                    <ColorPicker showText />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name="accentColor"
                    label="Accent Color"
                  >
                    <ColorPicker showText />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item
                    name="fontFamily"
                    label="Font Family"
                  >
                    <Select>
                      <Option value="Futura, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif">Futura (Default)</Option>
                      <Option value="Arial, sans-serif">Arial</Option>
                      <Option value="Helvetica, sans-serif">Helvetica</Option>
                      <Option value="Georgia, serif">Georgia</Option>
                      <Option value="Times New Roman, serif">Times New Roman</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          </TabPane>

          {/* Feature Toggles */}
          <TabPane tab={<span><BellOutlined />Features</span>} key="features">
            <Card title="Feature Toggles" style={{ marginBottom: '24px' }}>
              <Row gutter={[16, 16]}>
                <Col span={12}>
                  <Form.Item
                    name="enableReferralProgram"
                    label="Enable Referral Program"
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="enableEmailMarketing"
                    label="Enable Email Marketing"
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="enableAnalytics"
                    label="Enable Analytics"
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="enableUserRegistration"
                    label="Enable User Registration"
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="enableQuoteSystem"
                    label="Enable Quote System"
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>
                </Col>
              </Row>
            </Card>

            <Card title="Security Settings" style={{ marginBottom: '24px' }}>
              <Row gutter={[16, 16]}>
                <Col span={12}>
                  <Form.Item
                    name="requireEmailVerification"
                    label="Require Email Verification"
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="allowGuestQuotes"
                    label="Allow Guest Quotes"
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="maxQuotesPerUser"
                    label="Max Quotes Per User"
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      min={1}
                      max={100}
                      placeholder="5"
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="sessionTimeout"
                    label="Session Timeout (minutes)"
                  >
                    <Slider
                      min={5}
                      max={120}
                      marks={{
                        5: '5m',
                        30: '30m',
                        60: '1h',
                        120: '2h'
                      }}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          </TabPane>

          {/* SEO Settings */}
          <TabPane tab={<span><InfoCircleOutlined />SEO</span>} key="seo">
            <Card title="SEO Settings" style={{ marginBottom: '24px' }}>
              <Row gutter={[16, 16]}>
                <Col span={24}>
                  <Form.Item
                    name="metaTitle"
                    label="Meta Title"
                    rules={[{ required: true, message: 'Please enter meta title' }]}
                  >
                    <Input placeholder="Enter meta title" />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item
                    name="metaDescription"
                    label="Meta Description"
                    rules={[{ required: true, message: 'Please enter meta description' }]}
                  >
                    <TextArea rows={3} placeholder="Enter meta description" />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item
                    name="metaKeywords"
                    label="Meta Keywords"
                  >
                    <Input placeholder="Enter keywords separated by commas" />
                  </Form.Item>
                </Col>
              </Row>
            </Card>

            <Card title="Social Media Links" style={{ marginBottom: '24px' }}>
              <Row gutter={[16, 16]}>
                <Col span={12}>
                  <Form.Item
                    name="facebookUrl"
                    label="Facebook URL"
                  >
                    <Input placeholder="https://facebook.com/yourpage" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="twitterUrl"
                    label="Twitter URL"
                  >
                    <Input placeholder="https://twitter.com/yourhandle" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="instagramUrl"
                    label="Instagram URL"
                  >
                    <Input placeholder="https://instagram.com/yourhandle" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="linkedinUrl"
                    label="LinkedIn URL"
                  >
                    <Input placeholder="https://linkedin.com/company/yourcompany" />
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          </TabPane>

          {/* Maintenance */}
          <TabPane tab={<span><SecurityScanOutlined />Maintenance</span>} key="maintenance">
            <Card title="Maintenance Mode" style={{ marginBottom: '24px' }}>
              <Row gutter={[16, 16]}>
                <Col span={24}>
                  <Alert
                    message="Maintenance Mode"
                    description="When enabled, the site will show a maintenance message to all visitors except administrators."
                    type="info"
                    showIcon
                    style={{ marginBottom: '16px' }}
                  />
                </Col>
                <Col span={24}>
                  <Form.Item
                    name="maintenanceMode"
                    label="Enable Maintenance Mode"
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item
                    name="maintenanceMessage"
                    label="Maintenance Message"
                  >
                    <TextArea rows={3} placeholder="Enter maintenance message" />
                  </Form.Item>
                </Col>
              </Row>
            </Card>

            <Card title="Backup Settings" style={{ marginBottom: '24px' }}>
              <Row gutter={[16, 16]}>
                <Col span={12}>
                  <Form.Item
                    name="autoBackup"
                    label="Enable Auto Backup"
                    valuePropName="checked"
                  >
                    <Switch />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="backupFrequency"
                    label="Backup Frequency"
                  >
                    <Select>
                      <Option value="daily">Daily</Option>
                      <Option value="weekly">Weekly</Option>
                      <Option value="monthly">Monthly</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="backupRetention"
                    label="Backup Retention (days)"
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      min={1}
                      max={365}
                      placeholder="30"
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          </TabPane>
        </Tabs>

        {/* Action Buttons */}
        <Card style={{ marginTop: '24px' }}>
          <Row gutter={[16, 16]} justify="space-between" align="middle">
            <Col>
              <Space>
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<SaveOutlined />}
                  loading={loading}
                  disabled={!hasChanges}
                >
                  Save Settings
                </Button>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={loadSettings}
                >
                  Reload
                </Button>
                <Button
                  danger
                  onClick={handleResetSettings}
                >
                  Reset to Default
                </Button>
              </Space>
            </Col>
            <Col>
              <Space>
                <Upload
                  beforeUpload={handleImportSettings}
                  showUploadList={false}
                >
                  <Button icon={<UploadOutlined />}>
                    Import Settings
                  </Button>
                </Upload>
                <Button
                  onClick={handleExportSettings}
                >
                  Export Settings
                </Button>
              </Space>
            </Col>
          </Row>
          {hasChanges && (
            <Alert
              message="You have unsaved changes"
              description="Don't forget to save your changes before leaving this page."
              type="warning"
              showIcon
              style={{ marginTop: '16px' }}
            />
          )}
        </Card>
      </Form>
    </div>
  );
};

export default AdminSettings;
